import type { TranscriptionLanguage } from "@/common/types/transcriptionLanguage";

// The note language is the account preference `transcriptionLanguageId`
// (Settings → Transcription). The worker writes the note, letters, summaries
// and the stored transcript in that language, translating when the
// conversation was in another one. "Same as conversation" is a per-account
// choice kept on this device: while it is on, picking an input language also
// saves the matching note language.

const SAME_KEY_PREFIX = "rosc.noteLanguage.sameAsConversation.";

export const readSameAsConversation = (accountId?: string | null): boolean => {
  if (!accountId) return false;
  try {
    return localStorage.getItem(SAME_KEY_PREFIX + accountId) === "1";
  } catch {
    return false;
  }
};

export const writeSameAsConversation = (
  accountId: string | null | undefined,
  value: boolean,
): void => {
  if (!accountId) return;
  try {
    if (value) localStorage.setItem(SAME_KEY_PREFIX + accountId, "1");
    else localStorage.removeItem(SAME_KEY_PREFIX + accountId);
  } catch {
    // Storage blocked: the choice lasts for this page only.
  }
};

// Input (recording) language codes are the transcriber's; two differ from the
// note language codes stored in `transcription_languages`.
export const noteLanguageForInput = (
  inputCode: string | null | undefined,
  languages: readonly TranscriptionLanguage[],
): TranscriptionLanguage | undefined => {
  if (!inputCode || inputCode === "auto") return undefined;
  if (inputCode === "cmn")
    return languages.find((language) => language.name === "Mandarin");
  const code = inputCode === "jap" ? "jpn" : inputCode;
  return languages.find(
    (language) =>
      language.code === code &&
      language.name !== "Cantonese" &&
      language.name !== "Mandarin",
  );
};

// BCP 47 tags for Intl.DisplayNames, keyed by the stored English name (the
// codes are ISO 639-3 and "zho" is shared by Cantonese and Mandarin).
const DISPLAY_TAGS: Record<string, string> = {
  English: "en",
  French: "fr",
  Spanish: "es",
  Portuguese: "pt",
  Italian: "it",
  Japanese: "ja",
  Russian: "ru",
  Ukrainian: "uk",
  Cantonese: "yue",
};

const INPUT_DISPLAY_NAMES: Record<string, string> = {
  eng: "English",
  spa: "Spanish",
  fra: "French",
  por: "Portuguese",
  jap: "Japanese",
  cmn: "Mandarin",
};

const capitalize = (value: string, locale: string): string =>
  value.charAt(0).toLocaleUpperCase(locale) + value.slice(1);

/**
 * Language name in the interface language: "Français" for lists, or the
 * in-sentence form ("français") with `inline`.
 */
export const languageDisplayName = (
  englishName: string,
  locale: string | undefined,
  mandarin: string,
  { inline = false }: { inline?: boolean } = {},
): string => {
  if (englishName === "Mandarin") return mandarin;
  const tag = DISPLAY_TAGS[englishName];
  if (!tag || !locale) return englishName;
  try {
    const name = new Intl.DisplayNames([locale], { type: "language" }).of(tag);
    if (!name) return englishName;
    return inline ? name : capitalize(name, locale);
  } catch {
    return englishName;
  }
};

export const inputLanguageDisplayName = (
  inputCode: string,
  locale: string | undefined,
  mandarin: string,
): string => {
  const english = INPUT_DISPLAY_NAMES[inputCode];
  return english ? languageDisplayName(english, locale, mandarin) : inputCode;
};
